import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import Home from './components/Home';
import { PrivateRoute, PublicRoute } from './routes/ProtectedRoutes';
import SeeContent from './components/SeeContent';
import Camera from './components/Camera';
import Flashes from './components/Flashes';
import ProfileUpload from './components/ProfileUpload';
import Profile from './components/Profile';
import YourTrippins from './components/YourTrippins';
import TrippinPlaceSelect from './components/TrippinPlaceSelect';
import TrippinPhotoSelect from './components/TrippinPhotoSelect';
import TrippinReviewPost from './components/TrippinReviewPost';
import { connectSocket } from './utils/WebSocket';
import Notifications from './components/Notifications';
import PlaceView from "./components/PlaceView";
import Saved from "./components/Saved";
import UserFlashes from './components/UserFlashes';
import Flash from './components/Flash';
import StalkersList from './components/StalkersList';
import StalkingList from './components/StalkingList';
import EditCloseFriends from './components/EditCloseFirends';
import Search from './components/Search';
import { App as CapacitorApp } from '@capacitor/app';
import MyTrippinGrid from './components/MyTrippinGrid';
import TrippinsTabs from './components/TrippinTabs';
import MyTrippinTabs from './components/MyTrippinTabs';
import { PushNotifications } from '@capacitor/push-notifications';
import { isPlatform } from '@ionic/react';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import { SocketProvider } from "./context/SocketContext";
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext'; // adjust path if needed
import { unmountTrippinOverlay } from './utils/trippinOverlay';
import VerifyEmail from './components/Auth/VerifyEmail';
import RequireInit from './RequireInit';
import ManageSessions from './components/ManageSessions';
import "react-datepicker/dist/react-datepicker.css";
import SeeChats from './components/chat/SeeChats';
import ChatWindow from './components/chat/ChatWindow';
import ChatSettings from "./components/chat/ChatSettings";
import NewChat from './components/chat/NewChat';
import SeeContentChat from './components/chat/SeeContentChat';
import Test from './components/calls/Test';
import CreateGroupChat from './components/chat/CreateGroupChat';
import CallScreen from './components/calls/CallScreen';
import { getUsername, getProfilePicture } from "./utils/userUtils";
import { useSocket } from "./context/SocketContext";
import IncomingCall from "./components/calls/IncomingCall";
import PostExistingTripPage from './components/PostExistingTripPage';
import Settings from './components/Settings';


function AppShell({ children }) {
  const { incomingCall, setIncomingCall, socket } = useSocket();
  const navigate = useNavigate();

  const [callerName, setCallerName] = useState("");
  const [callerImage, setCallerImage] = useState(null);
  const [ringtoneAudio, setRingtoneAudio] = useState(null);

  useEffect(() => {
    if (incomingCall?.from) {
      getUsername(incomingCall.from).then(name => setCallerName(name));
      getProfilePicture(incomingCall.from).then(url => setCallerImage(url));

      const audio = new Audio("/assets/sounds/ringtone.mp3");
      audio.loop = true;
      audio.play().catch(err =>
        console.error("Autoplay blocked by browser:", err)
      );
      setRingtoneAudio(audio);

      if (navigator.vibrate) {
        navigator.vibrate([500, 300, 500, 300, 500, 300]);
      }
    }
    else {
      if (ringtoneAudio) {
        ringtoneAudio.pause();
        ringtoneAudio.currentTime = 0;
        setRingtoneAudio(null);
      }
      if (navigator.vibrate) {
        navigator.vibrate(0);
      }
    }
  }, [incomingCall]);

  useEffect(() => {
    if (!socket) return;

    const clearIncoming = (payload) => {
      if (incomingCall) {
        setIncomingCall(null);
      }
    };

    socket.on('call-canceled', clearIncoming);
    socket.on('call-rejected', clearIncoming);

    return () => {
      socket.off('call-canceled', clearIncoming);
      socket.off('call-rejected', clearIncoming);
    };
  }, [socket, incomingCall, setIncomingCall]);

  const accept = () => {
    if (!incomingCall) return;
    socket.emit("accept-call", { to: incomingCall.from });

    navigate(`/call/${incomingCall.from}`, {
      state: { peer: { id: incomingCall.from, name: callerName } },
    });

    setIncomingCall(null);
  };

  const decline = () => {
    if (!incomingCall) return;
    socket.emit("cancel-call", { to: incomingCall.from });
    socket.emit("reject-call", { to: incomingCall.from });
    setIncomingCall(null);
  };

  return (
    <>
      {incomingCall ? (
        <IncomingCall
          callerName={callerName || "Unknown"}
          callerImage={callerImage}
          onAccept={accept}
          onDecline={decline}
        />
      ) : (
        children
      )}
    </>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  // If the TrippinPostView overlay is mounted, close it whenever the route changes.
  // This prevents the overlay from staying visible when navigating via BottomNav/Links.
  useEffect(() => {
    unmountTrippinOverlay();
  }, [location?.key]);

  // Avoid destructuring before confirming auth is defined
  const accessToken = auth?.accessToken;
  const userId = auth?.userId;
  const initialized = auth?.initialized;

// Stripe publishable key for client-side init
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.STRIPE_PUBLISHABLE_KEY;
  useEffect(() => {
    try {
      if (userId) {
        connectSocket(userId);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }, [userId]);


  useEffect(() => {
    // ONLY run on mobile platforms
    if (isPlatform('android') && accessToken) {
      try {
        // Function to register for push notifications and save the token
        const registerPushNotifications = async () => {
          try {
            // Check the permission status
            const permissionStatus = await PushNotifications.checkPermissions();
            if (permissionStatus.receive !== 'granted') {
              permissionStatus = await PushNotifications.requestPermissions();
              console.log("Requested permission status:", permissionStatus);
            }
            if (permissionStatus.receive === 'granted') {
              console.log('Permission granted. Registering for push notifications...');
              // Handle successful registration
              PushNotifications.addListener('registration', async (token) => {
                console.log('Push registration success:', token.value);
                // Save the FCM token to the backend
                await fetch('https://api.trippin.icu/api/device-tokens', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify({ deviceToken: token.value }),
                })
                  .then(response => response.json())
                  .then(data => console.log("Token stored successfully:", data))
                  .catch(error => console.error("Error saving token:", error));
              });

              // Handle registration error
              PushNotifications.addListener('registrationError', (error) => {
                console.error('Push registration error:', error);
              });
              await PushNotifications.register();
            }
            else {
              console.warn('Push notifications permission not granted.');
            }
          } catch (error) {
            console.error('Error during push notification registration:', error);
          }
        };

        // Register for push notifications when the app is in the foreground
        CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
          if (isActive) {
            console.log('App is active. Checking push notification permissions...');
            await registerPushNotifications();
          }

        });

        registerPushNotifications();
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, [accessToken]);

  useEffect(() => {
    if (isPlatform('ios') && accessToken) {
      console.log("🔄 Waiting for FCM token from iOS...");

      // Listen for event from Swift (token will be sent from AppDelegate)
      window.addEventListener("FCMTokenReceived", async (event) => {
        const token = event.detail.token;
        console.log("✅ Received FCM Token from Native:", token);

        try {
          await fetch('https://api.trippin.icu/api/device-tokens', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ deviceToken: token }),
          });
          console.log("✅ Token stored successfully in backend");
        } catch (error) {
          console.error("❌ Error saving token:", error);
        }
      });

      console.log("📡 Waiting for FCM token event...");
    }
  }, [accessToken]);

  useEffect(() => {
    let backButtonListener;

    const handleBackButton = () => {
      if (location.pathname === '/home' || location.pathname === '/login') {
        // Exit app if on the home or login page
        CapacitorApp.exitApp();
      } else {
        // Navigate back
        navigate(-1);
      }
    };

    // Add listener for hardware back button
    const addBackButtonListener = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', handleBackButton);
    };

    addBackButtonListener();

    // Cleanup listener on component unmount
    return () => {
      if (backButtonListener && typeof backButtonListener.remove === 'function') {
        backButtonListener.remove(); // Properly remove the listener
      }
    };
  }, [location, navigate]);

  if ('serviceWorker' in navigator) {
    if (!window.Capacitor || !window.Capacitor.isNativePlatform()) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    } else {
      console.log('Skipping Service Worker for Capacitor WebView');
    }
  }

  return (
    <SocketProvider>
      <RequireInit>
        <AnimatePresence mode="wait">
          <AppShell>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/verify-email" element={<PublicRoute><VerifyEmail /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />
              <Route path="/privacypolicy" element={<PublicRoute><PrivacyPolicy /></PublicRoute>} />
              <Route path="/termsandconditions" element={<PublicRoute><TermsAndConditions /></PublicRoute>} />

              {/* Private Routes */}
              <Route path="*" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
              <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
              <Route path="/profile-upload" element={<PrivateRoute><ProfileUpload /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/profile/:userIdPar" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/yourtrippins/:trippinId/placesSelect" element={<PrivateRoute><TrippinPlaceSelect isTagged={false} /></PrivateRoute>} />
              <Route path="/taggedtrippins/:trippinId/placesSelect" element={<PrivateRoute><TrippinPlaceSelect isTagged={true} /></PrivateRoute>} />
              <Route path="/taggedtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={true} /></PrivateRoute>} />          <Route path="/yourtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={false} /></PrivateRoute>} />
              <Route path="/taggedtrippins/:trippinId/reviewPost" element={<PrivateRoute><TrippinReviewPost isTagged={true} /></PrivateRoute>} />
              <Route path="/yourtrippins/:trippinId/reviewPost" element={<PrivateRoute><TrippinReviewPost isTagged={false} /></PrivateRoute>} />
              <Route path="/yourtrippins" element={<PrivateRoute><YourTrippins /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
              <Route path="/trippinpostgrid" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/saved" element={<PrivateRoute><Saved /></PrivateRoute>} />
              <Route path="/places/:placeId" element={<PrivateRoute><PlaceView /></PrivateRoute>} />
              <Route path="/profile/:userId/stalkers" element={<PrivateRoute><StalkersList /></PrivateRoute>} />
              <Route path="/profile/:userId/stalking" element={<PrivateRoute><StalkingList /></PrivateRoute>} />
              <Route path="/user-flashes/:userId" element={<UserFlashes />} />
              <Route path="/flash/:flashId" element={<Flash />} />
              <Route path="/edit-close-friends" element={<PrivateRoute><EditCloseFriends /></PrivateRoute>} />
              <Route path="/mytrippins" element={<PrivateRoute><MyTrippinGrid /></PrivateRoute>} />
              <Route path="/tabs" element={<PrivateRoute><TrippinsTabs /></PrivateRoute>} />
              <Route path="/mytabs" element={<PrivateRoute><MyTrippinTabs /></PrivateRoute>} />
              <Route path="/yourtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={false} /></PrivateRoute>} />
              <Route path="/testing" element={<PrivateRoute><Test /></PrivateRoute>} />
              <Route path="/create_chat" element={<PrivateRoute><CreateGroupChat /></PrivateRoute>} />
              <Route path="/search" element={<PrivateRoute><Search /></PrivateRoute>} />
              <Route path="/manage-sessions" element={<PrivateRoute><ManageSessions /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
              <Route path="/flashes" element={<PrivateRoute><Flashes /></PrivateRoute>} />
              <Route path="/camera" element={<PrivateRoute><Camera /></PrivateRoute>} />
              <Route path="/see-content" element={<PrivateRoute><SeeContent /></PrivateRoute>} />
              <Route path="/chat/conversations" element={<PrivateRoute><SeeChats /></PrivateRoute>} />
              <Route path="/chat/:chatId" element={<PrivateRoute><ChatWindow /></PrivateRoute>} />
              <Route path="/chat/:chatId/settings" element={<ChatSettings />} />
              <Route path="/see-content-chat" element={<PrivateRoute><SeeContentChat /></PrivateRoute>} />
              <Route path="/call/:peerId" element={<CallScreen />} />
              <Route path="/chat/new" element={<PrivateRoute><NewChat /></PrivateRoute>} />
              <Route path="/post-existing-trip/details" element={<PrivateRoute><PostExistingTripPage /></PrivateRoute>} />
            </Routes>
          </AppShell>
        </AnimatePresence>
      </RequireInit>
    </SocketProvider>
  );
}

export default App;
